import React, { useEffect, useRef, useState, memo, useMemo } from "react";

import {
  needBorder,
  matrixBasis,
  centralIndex,
  defaultColors,
  getColorsMatrix,
  convertInto2DMatrix,
} from "./dropperUtils";
import { createPortal } from "react-dom";
import {
  StyledBorderWhite,
  StyledEyeDropperOverlay,
  StyledEyeDropperOverlaySquare,
  StyledEyeDropperOverlayWrapper,
  StyledHexLabel,
  StyledHexValue,
} from "../../styled/details/stylesTextPanel";

function Dropper({ context, onChange }) {
  const [colors, setColors] = useState(defaultColors);
  const [isVisible, setIsVisible] = useState(false);
  const centralColorRef = useRef("");
  const { matrixRepresantationData, clientRect } = useMemo(() => {
    if (context) {
      const ctx = context?.getContext("2d");
      const clientRect = context.getBoundingClientRect();
      const imageData = ctx.getImageData(
        0,
        0,
        context.width,
        context.height
      ).data;
      const matrixRepresantationData = convertInto2DMatrix(
        imageData,
        context.width * 4
      );
      return {
        clientRect,
        matrixRepresantationData,
      };
    }
    return {};
  }, [context]);

  centralColorRef.current = colors[centralIndex][centralIndex];

  useEffect(() => {
    if (context && clientRect && matrixRepresantationData) {
      const onMouseMove = (event) => {
        const { top: boundTop, left: boundLeft, width, height } = clientRect;
        const widthRatio = context.width / width || 1;
        const heightRatio = context.height / height || 1;
        const { clientX, clientY } = event;
        const nextColors = getColorsMatrix(
          matrixRepresantationData,
          clientX - boundLeft,
          clientY - boundTop,
          widthRatio,
          heightRatio
        );
        // onMouseOver and onMouseLeave works wrong, so we hide dropper when the 'clientX' biggger than 'boundLeft', same for clientY and boundTop
        if (clientX <= boundLeft || clientY <= boundTop) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }

        setColors(nextColors);
      };
      const onMouseOver = () => {
        setIsVisible(true);
      };
      const onMouseLeave = () => {
        setIsVisible(false);
      };

      const onClick = (e) => onChange(centralColorRef.current, true, e);

      context.addEventListener("mousemove", onMouseMove);
      context.addEventListener("mouseover", onMouseOver);
      context.addEventListener("mouseleave", onMouseLeave);
      context.addEventListener("mousedown", onClick);
      return () => {
        context.removeEventListener("mousemove", onMouseMove);
        context.removeEventListener("mouseover", onMouseOver);
        context.removeEventListener("mouseleave", onMouseLeave);
        context.removeEventListener("mousedown", onClick);
      };
    }
  }, [context, onChange, clientRect, matrixRepresantationData]);

  useEffect(() => {
    context.style.setProperty("cursor", "crosshair");

    return () => {
      context.style.setProperty("cursor", "unset");
    };
  });

  return isVisible
    ? createPortal(
        <StyledBorderWhite style={{ top: 80, right: 20 }}>
          <StyledEyeDropperOverlay
            style={{ borderColor: colors[centralIndex][centralIndex] }}
          >
            {matrixBasis.map((_, index) => (
              <StyledEyeDropperOverlayWrapper key={index}>
                {matrixBasis.map(($, itemIndex) => {
                  const isBorderNeeded = needBorder(itemIndex, index);
                  return (
                    <StyledEyeDropperOverlaySquare
                      key={itemIndex}
                      isBorderNeeded={isBorderNeeded}
                      style={{ background: colors[index][itemIndex] }}
                    />
                  );
                })}
              </StyledEyeDropperOverlayWrapper>
            ))}
            <StyledHexLabel>
              <StyledHexValue>
                {colors[centralIndex][centralIndex].toUpperCase()}
              </StyledHexValue>
            </StyledHexLabel>
          </StyledEyeDropperOverlay>
        </StyledBorderWhite>,
        document.body
      )
    : null;
}

export default memo(Dropper);
